/**
 * AdminPreferenceVisibilityPage - Pass 4f
 *
 * Admin control panel for which preference categories show on /me Defaults
 * and which fields are visible/required per category. All toggles persist to
 * `db.categories.preference_fields` so the frontend stays zero-hardcoding.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, ChevronDown, ChevronRight, Save, RefreshCw, Plus, Pencil, Upload, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MasterOptionEditor } from "@/components/admin/MasterOptionEditor";
import UniversalPreferenceCardPopup from "@/components/UniversalPreferenceCardPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const humanize = (s) =>
  String(s || "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

function Toggle({ checked, onChange, testId, label, hint }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        data-testid={testId}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          checked ? "bg-gray-900" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </button>
      <div>
        <span className="text-xs text-gray-700">{label}</span>
        {hint && <div className="text-[10px] text-gray-400">{hint}</div>}
      </div>
    </label>
  );
}

function FieldBlock({ category, field, onPatch, onAddOption, onEditOption, onDeleteOption, onPreview, onEditField }) {
  const [busy, setBusy] = useState(false);
  const patch = async (delta) => {
    setBusy(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/preference-visibility/field`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          category_id: category.category_id,
          field_id: field.field_id,
          patch: delta,
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      onPatch(field.field_id, delta);
      toast.success(`${field.label} updated`);
    } catch (e) {
      toast.error(`Update failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const opts = field.options || [];
  const hidden = field.default_visible === false;

  return (
    <div
      className={`rounded-xl border bg-white p-3 ${hidden ? "border-stone-200 opacity-60" : "border-stone-200"}`}
      data-testid={`field-block-${category.category_id}-${field.field_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-stone-900 truncate">{field.label}</h4>
            <span className="text-[10px] text-stone-400 px-1.5 py-0.5 rounded bg-stone-100 capitalize">{(field.type || "multi_select").replace(/_/g, " ")}</span>
            <span className="text-[10px] text-stone-400">{opts.length} options</span>
          </div>
          {field.help && <p className="text-[11px] text-stone-500 mt-0.5">{field.help}</p>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="ghost" onClick={() => onPreview && onPreview(field)} className="h-7 px-2 text-[10px]" data-testid={`field-preview-${field.field_id}`}>
            <Eye className="w-3 h-3 mr-1" />Preview
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEditField && onEditField(field)} className="h-7 px-2 text-[10px]" data-testid={`field-edit-${field.field_id}`}>
            <Pencil className="w-3 h-3" />
          </Button>
          {busy && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
        </div>
      </div>

      <div className="flex items-center gap-3 pb-2 border-b border-stone-100 mb-3">
        <Toggle checked={field.default_visible !== false} onChange={(v) => patch({ default_visible: v })} testId={`toggle-visible-${field.field_id}`} label={field.default_visible !== false ? "Visible" : "Hidden"} />
        <span className="text-stone-200">·</span>
        <Toggle checked={Boolean(field.default_required)} onChange={(v) => patch({ default_required: v })} testId={`toggle-required-${field.field_id}`} label={field.default_required ? "Required" : "Optional"} />
        <span className="text-stone-200">·</span>
        <Toggle checked={field.host_toggleable !== false} onChange={(v) => patch({ host_toggleable: v })} testId={`toggle-host-${field.field_id}`} label={field.host_toggleable !== false ? "Host can toggle" : "Locked"} />
      </div>

      {/* Option grid - exactly mirrors /me preferences card layout */}
      {(() => {
        const isMasterSourced = !!field.master_source || (field.master_options && field.master_options.length > 0);
        const display = isMasterSourced ? (field.master_options || []) : opts;

        if (display.length === 0) {
          return (
            <div className="py-4 text-center text-[11px] text-stone-400 border border-dashed border-stone-200 rounded-lg">
              {isMasterSourced
                ? <>No master records match this filter yet. Add entries in <a href="/admin/product/master-products" className="underline text-purple-600">Master Products</a>.</>
                : <>No options yet - click "+ Add option" to seed the first.</>}
            </div>
          );
        }

        return (
          <>
            {isMasterSourced && (
              <div className="flex items-center gap-2 mb-2 text-[10px] text-purple-700 bg-purple-50 border border-purple-100 rounded px-2 py-1" data-testid={`master-sync-banner-${field.field_id}`}>
                <Eye className="w-3 h-3" />
                Synced from <a href="/admin/product/master-products" className="underline font-medium">{field.master_source === "master_brands" ? "Master Brands" : "Master Products"}</a>
                {field.master_filter && Object.keys(field.master_filter).length > 0 && (
                  <span className="text-purple-500 ml-1">· filter: {Object.entries(field.master_filter).map(([k,v]) => `${k}=${v}`).join(" · ")}</span>
                )}
                <span className="ml-auto">{display.length} option{display.length !== 1 ? "s" : ""}</span>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid={`field-options-grid-${field.field_id}`}>
              {display.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    if (isMasterSourced) {
                      // Open the master record editor in a new tab
                      window.open("/admin/product/master-products", "_blank");
                    } else {
                      onEditOption && onEditOption(field, opt);
                    }
                  }}
                  className="group relative rounded-lg border border-stone-200 hover:border-purple-400 hover:shadow-sm transition p-2 flex items-center gap-2 text-left bg-white"
                  data-testid={`field-opt-${field.field_id}-${opt.id}`}
                >
                  {opt.image_url ? (
                    <img src={opt.image_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                  ) : opt.swatch_color ? (
                    <span className="w-9 h-9 rounded-full shrink-0 border border-stone-200" style={{ backgroundColor: opt.swatch_color }} />
                  ) : (
                    <span className="w-9 h-9 rounded-full shrink-0 border border-dashed border-stone-200 flex items-center justify-center">
                      <span className="text-[9px] text-stone-300">no img</span>
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-stone-900 truncate">{opt.label}</p>
                    {opt.brand && <p className="text-[10px] text-stone-400 truncate">{opt.brand}{opt.shade ? ` · ${opt.shade}` : ""}</p>}
                    {!opt.brand && opt.helper_text && <p className="text-[10px] text-stone-400 truncate">{opt.helper_text}</p>}
                    {(opt.linked_products || []).length > 0 && (
                      <p className="text-[10px] text-purple-600">{opt.linked_products.length} linked</p>
                    )}
                  </div>
                  {!isMasterSourced && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onDeleteOption && onDeleteOption(field, opt); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDeleteOption && onDeleteOption(field, opt); } }}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 cursor-pointer"
                      data-testid={`field-opt-del-${field.field_id}-${opt.id}`}
                    >
                      <X className="w-3 h-3" />
                    </span>
                  )}
                </button>
              ))}
              {!isMasterSourced && (
                <button
                  type="button"
                  onClick={onAddOption}
                  className="rounded-lg border border-dashed border-stone-300 hover:border-purple-400 hover:bg-purple-50/30 transition p-2 flex items-center justify-center gap-1 text-[11px] text-stone-500 hover:text-purple-700"
                  data-testid={`field-add-option-grid-${field.field_id}`}
                >
                  <Plus className="w-3.5 h-3.5" />Add option
                </button>
              )}
            </div>
          </>
        );
      })()}
    </div>
  );
}

function CategoryBlock({ category, onCategoryToggle, onFieldPatch, onReload }) {
  const [open, setOpen] = useState(category.category_id === "makeup");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorContext, setEditorContext] = useState(null); // { kind, fieldId?, optionId?, initial? }
  const [iconUploading, setIconUploading] = useState(false);
  const [previewField, setPreviewField] = useState(null);
  const fileRef = useRef(null);

  const uploadIcon = async (file) => {
    if (!file) return;
    setIconUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "categories");
      const r = await fetch(`${API}/upload/cloudinary`, { method: "POST", headers: auth(), body: fd });
      if (!r.ok) throw new Error("upload failed");
      const data = await r.json();
      const url = data.secure_url || data.url;
      const r2 = await fetch(`${API}/admin/categories/${category.category_id}/icon`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ icon_url: url }),
      });
      if (r2.ok) { toast.success("Category icon updated"); onReload && onReload(); }
    } catch (e) { toast.error("Upload failed"); }
    finally { setIconUploading(false); }
  };

  const handleEditorSave = async (draft) => {
    const ctx = editorContext;
    if (ctx?.kind === "field") {
      const r = await fetch(`${API}/admin/categories/${category.category_id}/preference-fields`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          label: draft.label, field_type: draft.field_type, description: draft.description,
          image_url: draft.image_url, swatch_color: draft.swatch_color, sort_order: draft.sort_order,
          section: ctx.section || "general",
          section_label: ctx.sectionLabel || (ctx.section ? humanize(ctx.section) : "General"),
          options: [],
        }),
      });
      if (r.ok) { toast.success("New field added"); onReload && onReload(); }
    } else if (ctx?.kind === "edit_field_meta") {
      // Edit existing field's label / type / helper text via the visibility patch endpoint.
      const r = await fetch(`${API}/admin/preference-visibility/field`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          category_id: category.category_id,
          field_id: ctx.fieldId,
          patch: {
            label: draft.label,
            help: draft.helper_text,
            type: draft.field_type,
          },
        }),
      });
      if (r.ok) { toast.success("Field updated"); onReload && onReload(); }
      else { toast.error("Update failed"); }
    } else if (ctx?.kind === "option") {
      const r = await fetch(`${API}/admin/categories/${category.category_id}/preference-fields/${ctx.fieldId}/options`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          label: draft.label, image_url: draft.image_url, description: draft.description,
          helper_text: draft.helper_text, swatch_color: draft.swatch_color,
          sort_order: draft.sort_order, linked_products: draft.linked_products || [],
        }),
      });
      if (r.ok) { toast.success("Option added"); onReload && onReload(); }
    } else if (ctx?.kind === "edit_option") {
      const r = await fetch(`${API}/admin/categories/${category.category_id}/preference-fields/${ctx.fieldId}/options/${ctx.optionId}`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          label: draft.label, image_url: draft.image_url, description: draft.description,
          helper_text: draft.helper_text, swatch_color: draft.swatch_color,
          sort_order: draft.sort_order, linked_products: draft.linked_products || [],
        }),
      });
      if (r.ok) { toast.success("Option updated"); onReload && onReload(); }
      else { toast.error("Update failed"); }
    }
  };

  const handleDeleteOption = async (field, opt) => {
    if (!window.confirm(`Delete option "${opt.label}"?`)) return;
    const r = await fetch(`${API}/admin/categories/${category.category_id}/preference-fields/${field.field_id}/options/${opt.id}`, {
      method: "DELETE", headers: auth(),
    });
    if (r.ok) { toast.success("Option deleted"); onReload && onReload(); }
    else { toast.error("Delete failed"); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`cat-block-${category.category_id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`cat-toggle-${category.category_id}`}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
      >
        {category.icon_url ? (
          <img src={category.icon_url} alt="" className="w-7 h-7" />
        ) : (
          <div className="w-7 h-7 bg-gray-100 rounded" />
        )}
        <div className="flex-1 text-left">
          <div className="text-sm font-semibold text-gray-900">{category.label}</div>
          <div className="text-xs text-gray-500">{category.field_count} fields</div>
        </div>
        <Toggle
          checked={category.show_in_me_defaults}
          onChange={(v) => onCategoryToggle(category.category_id, v)}
          testId={`cat-show-${category.category_id}`}
          label={category.show_in_me_defaults ? "Shown on /me" : "Hidden from /me"}
        />
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="px-4 pb-3">
          {/* Per-category icon upload + Add field - Slice C */}
          <div className="flex items-center justify-between gap-2 py-2 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-gray-500">Category icon</span>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadIcon(e.target.files?.[0])} data-testid={`cat-icon-file-${category.category_id}`} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={iconUploading} data-testid={`cat-icon-upload-${category.category_id}`}>
                {iconUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                {category.icon_url ? "Replace" : "Upload"}
              </Button>
            </div>
            <Button size="sm" onClick={() => { setEditorContext({ kind: "field" }); setEditorOpen(true); }} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid={`cat-add-field-${category.category_id}`}>
              <Plus className="w-3 h-3 mr-1" />Add field
            </Button>
          </div>

          {category.fields.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-500">No preference fields configured. Click "+ Add field".</div>
          ) : (
            <div className="space-y-4 mt-3" data-testid={`cat-sections-${category.category_id}`}>
              {(() => {
                // Group fields by section_label / section, preserving original order
                const sections = [];
                const seen = new Map();
                for (const f of category.fields) {
                  const key = f.section || f.section_label || "general";
                  const label = f.section_label || (f.section ? humanize(f.section) : "General");
                  if (!seen.has(key)) {
                    seen.set(key, { key, label, fields: [] });
                    sections.push(seen.get(key));
                  }
                  seen.get(key).fields.push(f);
                }
                return sections.map((sec) => (
                  <section key={sec.key} className="space-y-2" data-testid={`cat-section-${category.category_id}-${sec.key}`}>
                    <div className="flex items-center gap-2 px-1">
                      <h3 className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{sec.label}</h3>
                      <span className="text-[10px] text-stone-300">·</span>
                      <span className="text-[10px] text-stone-400">{sec.fields.length} field{sec.fields.length !== 1 ? "s" : ""}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] text-stone-500 hover:text-purple-700 ml-auto"
                        onClick={() => { setEditorContext({ kind: "field", section: sec.key, sectionLabel: sec.label }); setEditorOpen(true); }}
                        data-testid={`cat-section-add-field-${category.category_id}-${sec.key}`}
                      >
                        <Plus className="w-3 h-3 mr-1" />Add field
                      </Button>
                    </div>
                    {sec.fields.map((f) => (
                      <FieldBlock
                        key={f.field_id}
                        category={category}
                        field={f}
                        onPatch={(fid, delta) => onFieldPatch(category.category_id, fid, delta)}
                        onAddOption={() => { setEditorContext({ kind: "option", fieldId: f.field_id }); setEditorOpen(true); }}
                        onEditOption={(field, opt) => {
                          setEditorContext({
                            kind: "edit_option",
                            fieldId: field.field_id,
                            optionId: opt.id,
                            initial: {
                              label: opt.label,
                              image_url: opt.image_url,
                              description: opt.description,
                              helper_text: opt.helper_text,
                              swatch_color: opt.swatch_color || "",
                              sort_order: opt.sort_order ?? 100,
                              linked_products: opt.linked_products || [],
                            },
                          });
                          setEditorOpen(true);
                        }}
                        onDeleteOption={handleDeleteOption}
                        onPreview={(field) => setPreviewField(field)}
                        onEditField={(field) => setEditorContext({ kind: "edit_field_meta", fieldId: field.field_id, initial: { label: field.label, helper_text: field.help, sort_order: field.sort_order ?? 100, swatch_color: "", field_type: field.type } }) || setEditorOpen(true)}
                      />
                    ))}
                  </section>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      <MasterOptionEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleEditorSave}
        showSelectionType={editorContext?.kind === "field" || editorContext?.kind === "edit_field_meta"}
        title={
          editorContext?.kind === "field" ? `New field for ${category.label}` :
          editorContext?.kind === "edit_field_meta" ? "Edit field" :
          editorContext?.kind === "edit_option" ? "Edit option" :
          "New option"
        }
        testid={`opt-editor-${category.category_id}`}
        initial={editorContext?.initial || { field_type: "multi_select", swatch_color: "", sort_order: 100 }}
      />

      {/* Slice E - live preview mirroring /me preference card popup */}
      {previewField && (
        <FieldOptionsPreview
          category={category}
          field={previewField}
          onClose={() => setPreviewField(null)}
        />
      )}
    </div>
  );
}

/**
 * FieldOptionsPreview - Slice E live preview.
 * Shows a side-drawer with each option rendered exactly as it appears
 * in the /me preferences popup grid (image swatch + label + helper).
 * Also shows a UniversalPreferenceCardPopup with sample tags so admins can
 * verify the option labels look right on the canonical client card.
 */
function FieldOptionsPreview({ category, field, onClose }) {
  const opts = field.options || [];
  const [picked, setPicked] = useState([]);
  const [showCard, setShowCard] = useState(false);

  const toggleOpt = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const tagLabels = picked.map((id) => opts.find((o) => o.id === id)?.label).filter(Boolean);
  const lightHex = category.color_light || category.light_hex || "#f5f5f4";

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} data-testid="pref-preview-backdrop" />
      <aside className="fixed top-0 right-0 h-full w-[420px] bg-white shadow-xl z-50 overflow-y-auto" data-testid="pref-preview-drawer">
        <div className="sticky top-0 bg-white border-b border-stone-100 px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-stone-400">{category.label}</p>
            <h3 className="text-sm font-semibold truncate">{field.label} - Live preview</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-[11px] text-stone-500">
            This mirrors what clients see on <code className="bg-stone-100 px-1 rounded">/me preferences</code>.
            Click options to simulate how the canonical client preference card renders the selection.
          </p>

          {/* Option grid - same rendering as /me */}
          <div className="grid grid-cols-2 gap-2" data-testid="pref-preview-grid">
            {opts.length === 0 && (
              <div className="col-span-2 text-center text-xs text-stone-400 py-8 border border-dashed rounded-lg">
                No options yet. Add some via "+ Add option".
              </div>
            )}
            {opts.map((opt) => {
              const sel = picked.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggleOpt(opt.id)}
                  className={`relative rounded-lg border-2 p-2 text-left transition ${sel ? "border-black bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}
                  data-testid={`pref-preview-opt-${opt.id}`}
                >
                  <div className="flex items-center gap-2">
                    {opt.image_url ? (
                      <img src={opt.image_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <span className="w-9 h-9 rounded-full" style={{ backgroundColor: opt.swatch_color || lightHex }} />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-stone-900 truncate">{opt.label}</p>
                      {opt.helper_text && <p className="text-[10px] text-stone-400 truncate">{opt.helper_text}</p>}
                      {(opt.linked_products || []).length > 0 && (
                        <p className="text-[10px] text-purple-600">{opt.linked_products.length} linked product(s)</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="pt-3 border-t border-stone-100 flex items-center justify-between">
            <span className="text-[11px] text-stone-500">{picked.length} selected</span>
            <Button size="sm" variant="outline" onClick={() => setShowCard(true)} disabled={picked.length === 0} data-testid="pref-preview-show-card">
              <Eye className="w-3 h-3 mr-1" />See client card
            </Button>
          </div>
        </div>
      </aside>

      <UniversalPreferenceCardPopup
        open={showCard}
        onOpenChange={setShowCard}
        client={{ name: "Sample client", service: `${category.label} service`, date: "Saved preferences" }}
        tagLabels={tagLabels}
        categoryLightHex={lightHex}
        testIdPrefix="pref-preview-card"
      />
    </>
  );
}

export default function AdminPreferenceVisibilityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/preference-visibility`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setData(await res.json());
    } catch (e) {
      toast.error(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data?.categories) return [];
    if (filter === "shown") return data.categories.filter((c) => c.show_in_me_defaults);
    if (filter === "hidden") return data.categories.filter((c) => !c.show_in_me_defaults);
    return data.categories;
  }, [data, filter]);

  const handleCategoryToggle = async (cid, show) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/preference-visibility/category`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ category_id: cid, show_in_me_defaults: show }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setData((d) => ({
        ...d,
        categories: d.categories.map((c) => c.category_id === cid ? { ...c, show_in_me_defaults: show } : c),
      }));
      toast.success(`${cid} ${show ? "shown on /me" : "hidden from /me"}`);
    } catch (e) {
      toast.error(`Toggle failed: ${e.message}`);
    }
  };

  const handleFieldPatch = (cid, fid, delta) => {
    setData((d) => ({
      ...d,
      categories: d.categories.map((c) => {
        if (c.category_id !== cid) return c;
        return {
          ...c,
          fields: c.fields.map((f) => f.field_id === fid ? { ...f, ...delta } : f),
        };
      }),
    }));
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-pref-visibility-page">
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Preference Visibility</h1>
            <p className="text-sm text-stone-500 mt-1">
              Control which preference categories show on /me Defaults and which fields are visible / required across services.
            </p>
          </div>
          <button onClick={load} data-testid="refresh-btn" className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-stone-300 rounded-lg hover:border-black">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 mb-4">
          {[
            { id: "all", label: "All categories" },
            { id: "shown", label: "Shown on /me" },
            { id: "hidden", label: "Hidden from /me" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-3 py-1.5 text-xs rounded-full border ${
                filter === f.id ? "bg-black text-white border-black" : "bg-white border-stone-300 hover:border-black"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <CategoryBlock
                key={c.category_id}
                category={c}
                onCategoryToggle={handleCategoryToggle}
                onFieldPatch={handleFieldPatch}
                onReload={load}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
